/* ── PC Compatibility Checker ───────────────────────────────────────────────
   Shared, global component. Build profiles (Brand/Socket/Chipset)
   write to a localStorage "builds" store and dispatch the same
   `vehicle:selected` CustomEvent on window — no app, no metafield, works
   out of the box for every merchant.
   ───────────────────────────────────────────────────────────────────────── */
(() => {
  const dialog = document.querySelector('[data-vfm-dialog]');
  if (!dialog) return;

  const GARAGE_KEY = 'circuit:build-garage';
  const ACTIVE_KEY = 'circuit:build-active-id';
  const DEVICE_DATA_URL = dialog.dataset.deviceDataUrl || '';

  const esc = (s) => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  const VALID_VIN = /^[A-HJ-NPR-Z0-9]{17}$/;

  let deviceDataCache = null;
  let screenStack = ['home'];

  /* ── Garage (localStorage) ── */
  const getGarage = () => {
    try {
      return JSON.parse(localStorage.getItem(GARAGE_KEY) || '[]');
    } catch (_) {
      return [];
    }
  };

  const setGarage = (list) => {
    try {
      localStorage.setItem(GARAGE_KEY, JSON.stringify(list));
    } catch (_) {}
    // Decoupled from `vehicle:selected` — that only fires on an active-vehicle
    // change, but the "My Garage" header badge needs to update any time the
    // garage list itself changes (e.g. removing a non-active saved vehicle).
    window.dispatchEvent(new CustomEvent('vehicle:garage-changed', { detail: list }));
  };

  const getActiveId = () => {
    try {
      return localStorage.getItem(ACTIVE_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const setActiveId = (id) => {
    try {
      localStorage.setItem(ACTIVE_KEY, id || '');
    } catch (_) {}
  };

  // A device picker built from a "brand > series > model" nav menu can have
  // a model entry titled e.g. "Acer 11 C721" (brand repeated for clarity
  // inside the menu) — joining brand + series + model verbatim would then
  // read "Acer Chromebook Acer 11 C721". Drop any part already contained in
  // a later part so the brand/series aren't repeated, without touching the
  // model's own text.
  const buildLabel = (v) => {
    const parts = [v.year, v.make, v.model].filter(Boolean);
    return parts
      .filter((part, i) => {
        const partLower = part.toLowerCase();
        return !parts.slice(i + 1).some((later) => later.toLowerCase().includes(partLower));
      })
      .join(' ');
  };

  const dispatchVehicleSelected = (vehicle) => {
    window.dispatchEvent(new CustomEvent('vehicle:selected', { detail: vehicle }));
  };

  const addVehicle = (vehicle) => {
    const garage = getGarage();
    const id = vehicle.vin
      ? `vin-${vehicle.vin}`
      : `ymm-${vehicle.year}-${vehicle.make}-${vehicle.model}`.toLowerCase().replace(/\s+/g, '-');
    const entry = { ...vehicle, id, label: buildLabel(vehicle) };
    const existingIndex = garage.findIndex((v) => v.id === id);
    if (existingIndex > -1) garage[existingIndex] = entry; else garage.push(entry);
    setGarage(garage);
    setActiveId(id);
    dispatchVehicleSelected(entry);
    return entry;
  };

  const removeVehicle = (id) => {
    setGarage(getGarage().filter((v) => v.id !== id));
    if (getActiveId() === id) {
      setActiveId('');
      dispatchVehicleSelected(null);
    }
  };

  const selectVehicle = (id) => {
    const vehicle = getGarage().find((v) => v.id === id);
    if (!vehicle) return;
    setActiveId(id);
    dispatchVehicleSelected(vehicle);
  };

  /* ── Screen navigation ── */
  const showScreen = (name) => {
    dialog.querySelectorAll('[data-vfm-screen]').forEach((el) => {
      el.hidden = el.dataset.vfmScreen !== name;
    });
    // 'home' and 'vin' are both top-level tab destinations; everything else
    // (ymm, vin-help) is a sub-screen reached via a goto/back flow.
    const isTopLevel = name === 'home' || name === 'vin';
    const backBtn = dialog.querySelector('[data-vfm-back]');
    if (backBtn) backBtn.hidden = isTopLevel;
    const tabsEl = dialog.querySelector('[data-vfm-tabs]');
    if (tabsEl) tabsEl.hidden = !isTopLevel;
  };

  const navigateTo = (name) => {
    screenStack.push(name);
    showScreen(name);
  };

  const navigateBack = () => {
    if (screenStack.length > 1) screenStack.pop();
    showScreen(screenStack[screenStack.length - 1]);
  };

  const resetToHome = () => {
    screenStack = ['home'];
    showScreen('home');
  };

  /* ── Garage rendering ── */
  // Captured once so the empty-garage state always reflects whatever the
  // merchant set as the screen subtitle in the Customizer, instead of a
  // hardcoded string here.
  const defaultHomeSubtitle = dialog.querySelector('[data-vfm-home-subtitle]')?.textContent || '';

  const renderGarage = () => {
    const garageEl = dialog.querySelector('[data-vfm-garage]');
    const listEl = dialog.querySelector('[data-vfm-garage-list]');
    const subtitleEl = dialog.querySelector('[data-vfm-home-subtitle]');
    if (!garageEl || !listEl) return;

    const garage = getGarage();

    if (subtitleEl) {
      subtitleEl.textContent = garage.length
        ? 'Add another device, or pick one below to shop compatible components.'
        : defaultHomeSubtitle;
    }

    if (!garage.length) {
      garageEl.hidden = true;
      listEl.innerHTML = '';
      return;
    }

    const activeId = getActiveId();
    garageEl.hidden = false;
    listEl.innerHTML = garage
      .map(
        (v) => `
        <li class="vfm-garage__item${v.id === activeId ? ' is-active' : ''}">
          <button type="button" data-vfm-garage-select data-id="${esc(v.id)}">${esc(v.label)}</button>
          <button type="button" data-vfm-garage-remove data-id="${esc(v.id)}" aria-label="Remove">
            <svg viewBox="0 0 24 24" width="11" height="11" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"/></svg>
          </button>
        </li>`
      )
      .join('');
  };

  /* ── Status helper ── */
  const setStatus = (el, message, type) => {
    if (!el) return;
    el.classList.remove('is-error', 'is-success');
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = message;
    if (type) el.classList.add(type);
  };

  /* ── Combobox factory — type-to-filter replacement for <select> ── */
  const makeCombobox = (container) => {
    if (!container) return null;
    const input = container.querySelector('.vfm-combobox__input');
    const listEl = container.querySelector('[data-vfm-cb-list]');
    if (!input || !listEl) return null;

    let allItems = [];
    let selectedValue = '';
    let activeIdx = -1;
    let onChangeCb = null;

    const esc = (s) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const getFiltered = () => {
      const q = input.value.trim().toLowerCase();
      if (!q || q === selectedValue.toLowerCase()) return allItems;
      return allItems.filter((item) => item.toLowerCase().includes(q));
    };

    const renderList = () => {
      const items = getFiltered();
      activeIdx = -1;
      listEl.innerHTML = items.length
        ? items.map((item) => `<li role="option" tabindex="-1" data-vfm-cb-item="${esc(item)}">${esc(item)}</li>`).join('')
        : '<li class="vfm-cb-empty">No results</li>';
    };

    const openList = () => {
      if (input.disabled) return;
      renderList();
      listEl.hidden = false;
      container.classList.add('is-open');
      input.setAttribute('aria-expanded', 'true');
    };

    const closeList = () => {
      listEl.hidden = true;
      container.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
      activeIdx = -1;
      input.value = selectedValue; // restore to confirmed value (or '' to show placeholder)
    };

    const confirmItem = (value) => {
      selectedValue = value;
      input.value = value;
      closeList();
      if (onChangeCb) onChangeCb(value);
    };

    const highlightAt = (idx) => {
      const rows = listEl.querySelectorAll('[data-vfm-cb-item]');
      rows.forEach((el) => el.classList.remove('is-active'));
      activeIdx = Math.max(0, Math.min(idx, rows.length - 1));
      if (rows[activeIdx]) {
        rows[activeIdx].classList.add('is-active');
        rows[activeIdx].scrollIntoView({ block: 'nearest' });
      }
    };

    input.addEventListener('focus', () => {
      input.select();
      openList();
    });

    input.addEventListener('input', () => {
      selectedValue = '';
      renderList();
      if (listEl.hidden) openList();
    });

    input.addEventListener('keydown', (e) => {
      const rows = listEl.querySelectorAll('[data-vfm-cb-item]');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (listEl.hidden) openList();
        highlightAt(activeIdx < 0 ? 0 : activeIdx + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        highlightAt(activeIdx <= 0 ? 0 : activeIdx - 1);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (activeIdx >= 0 && rows[activeIdx]) {
          confirmItem(rows[activeIdx].dataset.vfmCbItem);
        } else if (rows.length === 1) {
          confirmItem(rows[0].dataset.vfmCbItem);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeList();
      }
    });

    input.addEventListener('blur', () => {
      window.setTimeout(closeList, 150);
    });

    listEl.addEventListener('mousedown', (e) => {
      e.preventDefault(); // keep input focused so blur doesn't fire first
      const item = e.target.closest('[data-vfm-cb-item]');
      if (item) confirmItem(item.dataset.vfmCbItem);
    });

    container.querySelector('.vfm-combobox__chevron')?.addEventListener('mousedown', (e) => {
      e.preventDefault();
      if (listEl.hidden) { input.focus(); openList(); } else closeList();
    });

    return {
      getValue: () => selectedValue,
      setOptions: (items) => { allItems = items; },
      setEnabled: (placeholder) => {
        input.disabled = false;
        container.classList.remove('vfm-combobox--disabled');
        if (placeholder !== undefined) input.placeholder = placeholder;
      },
      setDisabled: (placeholder) => {
        input.disabled = true;
        container.classList.add('vfm-combobox--disabled');
        if (placeholder !== undefined) input.placeholder = placeholder;
        selectedValue = '';
        input.value = '';
        allItems = [];
        closeList();
      },
      reset: (placeholder) => {
        selectedValue = '';
        input.value = '';
        allItems = [];
        if (placeholder !== undefined) input.placeholder = placeholder;
      },
      onChange: (cb) => { onChangeCb = cb; },
    };
  };

  /* ── Device catalogue: nav menu (preferred) → JSON asset (fallback) ── */
  const menuDataToDeviceData = (arr) => {
    const out = {};
    for (const brand of arr) {
      out[brand.label] = {};
      for (const series of (brand.series || [])) {
        out[brand.label][series.label] = series.models || [];
      }
    }
    return out;
  };

  const loadDeviceData = async () => {
    if (deviceDataCache) return deviceDataCache;
    // Prefer the nav-menu data island rendered server-side by Liquid
    const menuEl = dialog.querySelector('[data-vfm-menu-data]');
    if (menuEl) {
      try {
        const arr = JSON.parse(menuEl.textContent);
        if (arr.length) { deviceDataCache = menuDataToDeviceData(arr); return deviceDataCache; }
      } catch (_) { /* fall through to asset */ }
    }
    // Fallback: fetch device-data.json asset
    if (!DEVICE_DATA_URL) throw new Error('No device data configured');
    const resp = await fetch(DEVICE_DATA_URL);
    if (!resp.ok) throw new Error('Failed to load device catalogue');
    deviceDataCache = await resp.json();
    return deviceDataCache;
  };

  /* ── Year / Make / Model cascade (combobox) ── */
  const yearCb = makeCombobox(dialog.querySelector('[data-vfm-year]'));
  const makeCb = makeCombobox(dialog.querySelector('[data-vfm-make]'));
  const modelCb = makeCombobox(dialog.querySelector('[data-vfm-model]'));
  const ymmSubmit = dialog.querySelector('[data-vfm-ymm-submit]');
  const ymmFind = dialog.querySelector('[data-vfm-ymm-find]');
  const ymmStatus = dialog.querySelector('[data-vfm-ymm-status]');
  const FIND_BASE_URL = ymmFind?.dataset.findUrl || '/search?q=';

  const updateYmmSubmitState = () => {
    const ready = !!(yearCb?.getValue() && makeCb?.getValue() && modelCb?.getValue());
    if (ymmSubmit) ymmSubmit.disabled = !ready;
    if (ymmFind) ymmFind.disabled = !ready;
  };

  const resetYmmForm = () => {
    yearCb?.reset('Select Brand');
    makeCb?.setDisabled('Select Brand first');
    modelCb?.setDisabled('Select Series first');
    updateYmmSubmitState();
  };

  // Populate brand options from device catalogue
  loadDeviceData().then((data) => {
    const brands = Object.keys(data).sort((a, b) => a.localeCompare(b));
    yearCb?.setOptions(brands);
  }).catch(() => {
    setStatus(ymmStatus, 'Could not load brand data. Please refresh the page.', 'is-error');
  });

  yearCb?.onChange(async (brand) => {
    setStatus(ymmStatus, '', null);
    makeCb?.setDisabled('Loading series…');
    modelCb?.setDisabled('Select Series first');
    updateYmmSubmitState();
    if (!brand) return;
    try {
      const data = await loadDeviceData();
      const series = Object.keys(data[brand] || {}).sort((a, b) => a.localeCompare(b));
      if (!series.length) { makeCb?.setDisabled('No series found'); return; }
      makeCb?.setOptions(series);
      makeCb?.setEnabled('Select Series');
    } catch (_) {
      makeCb?.setDisabled('Unable to load series');
      setStatus(ymmStatus, 'Could not load series data. Please try again.', 'is-error');
    }
    updateYmmSubmitState();
  });

  makeCb?.onChange(async (series) => {
    setStatus(ymmStatus, '', null);
    modelCb?.setDisabled('Loading models…');
    updateYmmSubmitState();
    const brand = yearCb?.getValue();
    if (!series || !brand) return;
    try {
      const data = await loadDeviceData();
      const models = (data[brand]?.[series] || []).slice().sort((a, b) => a.localeCompare(b));
      if (!models.length) { modelCb?.setDisabled('No models found for this series'); return; }
      modelCb?.setOptions(models);
      modelCb?.setEnabled('Select Model');
    } catch (_) {
      modelCb?.setDisabled('Unable to load models');
      setStatus(ymmStatus, 'Could not load models. Please try again.', 'is-error');
    }
    updateYmmSubmitState();
  });

  modelCb?.onChange(updateYmmSubmitState);

  const buildVehicleFromForm = () => ({
    year: yearCb.getValue(),
    make: makeCb.getValue(),
    model: modelCb.getValue(),
    engine: '',
    vin: '',
  });

  ymmFind?.addEventListener('click', () => {
    const vehicle = buildVehicleFromForm();
    addVehicle(vehicle);
    renderGarage();
    const query = encodeURIComponent([vehicle.year, vehicle.make].filter(Boolean).join(' '));
    window.location.href = FIND_BASE_URL + query;
  });

  ymmSubmit?.addEventListener('click', () => {
    const vehicle = buildVehicleFromForm();
    addVehicle(vehicle);
    setStatus(ymmStatus, `Added ${buildLabel(vehicle)} to your bench.`, 'is-success');
    renderGarage();
    window.setTimeout(() => {
      resetYmmForm();
      setStatus(ymmStatus, '', null);
      resetToHome();
    }, 900);
  });

  /* ── VIN decode ── */
  const vinInput = dialog.querySelector('[data-vfm-vin-input]');
  const vinSubmit = dialog.querySelector('[data-vfm-vin-submit]');
  const vinStatus = dialog.querySelector('[data-vfm-vin-status]');

  const cleanVin = (value) => value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17);

  vinInput?.addEventListener('input', () => {
    vinInput.value = cleanVin(vinInput.value);
  });

  const decodeVin = async (vin) => {
    const response = await fetch(`${API_ROOT}/DecodeVinValues/${vin}?format=json`);
    if (!response.ok) throw new Error('VIN lookup failed. Please try again.');
    const data = await response.json();
    const result = data.Results && data.Results[0];
    if (!result) throw new Error('No data returned for this VIN.');

    // vPIC still returns best-effort decoded data even on a malformed VIN —
    // ErrorCode can be a comma-joined list, so check the first code only.
    const firstErrorCode = String(result.ErrorCode || '0').split(',')[0].trim();
    if (firstErrorCode !== '0' && firstErrorCode !== '') {
      throw new Error(result.ErrorText || 'This VIN could not be decoded. Please double-check it.');
    }

    const displacement = parseFloat(result.DisplacementL);
    const engineParts = [
      Number.isFinite(displacement) ? `${displacement.toFixed(1)}L` : '',
      result.EngineConfiguration || '',
      result.EngineCylinders ? `${result.EngineCylinders} Cyl` : ''
    ].filter(Boolean);

    return {
      vin,
      year: result.ModelYear || '',
      make: result.Make || '',
      model: result.Model || '',
      engine: engineParts.join(' ')
    };
  };

  vinSubmit?.addEventListener('click', async () => {
    const vin = cleanVin(vinInput?.value || '');

    if (!VALID_VIN.test(vin)) {
      setStatus(vinStatus, 'Please enter a valid 17-character VIN.', 'is-error');
      return;
    }

    vinSubmit.disabled = true;
    setStatus(vinStatus, 'Decoding VIN…', null);

    try {
      const vehicle = await decodeVin(vin);
      addVehicle(vehicle);
      setStatus(vinStatus, `Added ${buildLabel(vehicle)} to your garage.`, 'is-success');
      renderGarage();
      window.setTimeout(() => {
        if (vinInput) vinInput.value = '';
        setStatus(vinStatus, '', null);
        resetToHome();
      }, 900);
    } catch (error) {
      if (error instanceof TypeError) {
        setStatus(vinStatus, 'VIN lookup is temporarily unavailable. Please try again later.', 'is-error');
      } else {
        setStatus(vinStatus, error.message, 'is-error');
      }
    } finally {
      vinSubmit.disabled = false;
    }
  });

  /* ── Delegated clicks within the dialog ── */
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) {
      dialog.close();
      return;
    }

    const selectBtn = e.target.closest('[data-vfm-garage-select]');
    if (selectBtn) {
      selectVehicle(selectBtn.dataset.id);
      dialog.close();
      return;
    }

    const removeBtn = e.target.closest('[data-vfm-garage-remove]');
    if (removeBtn) {
      removeVehicle(removeBtn.dataset.id);
      renderGarage();
      return;
    }

    const gotoBtn = e.target.closest('[data-vfm-goto]');
    if (gotoBtn) {
      navigateTo(gotoBtn.dataset.vfmGoto);
      return;
    }

    if (e.target.closest('[data-vfm-back]')) {
      navigateBack();
      return;
    }

    if (e.target.closest('[data-vfm-close]')) {
      dialog.close();
    }
  });

  dialog.addEventListener('close', resetToHome);

  /* ── Global open trigger — works from anywhere on the page ── */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-vfm-open]');
    if (!opener) return;
    e.preventDefault();
    renderGarage();
    dialog.showModal();
    // Optional deep-link: data-vfm-open-to="vin" or data-vfm-open-to="tire"
    const openTo = opener.dataset.vfmOpenTo;
    if (openTo === 'vin') {
      dialog.querySelector('[data-vfm-tab="vin"]')?.click();
    } else if (openTo === 'tire') {
      dialog.querySelector('[data-vfm-tab="tire"]')?.click();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && dialog.open) dialog.close();
  });

  /* ── Sync any header/trigger label elements with the active vehicle ── */
  const syncHeaderLabels = (vehicle) => {
    document.querySelectorAll('[data-vfm-header-label]').forEach((el) => {
      el.textContent = vehicle ? buildLabel(vehicle) : (el.dataset.vfmLabelDefault || 'Add a device');
    });
    // The "YOUR VEHICLE" eyebrow only makes sense once a vehicle is actually
    // saved — showing it next to the "Add a vehicle" placeholder reads oddly.
    document.querySelectorAll('[data-vfm-header-eyebrow]').forEach((el) => {
      el.hidden = !vehicle;
    });
  };

  window.addEventListener('vehicle:selected', (e) => syncHeaderLabels(e.detail));
  syncHeaderLabels(getActiveVehicle());

  /* ── Sync the "My Garage" header badge with the saved-vehicle count ── */
  const syncGarageBadges = () => {
    const count = getGarage().length;
    document.querySelectorAll('[data-garage-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  };

  window.addEventListener('vehicle:garage-changed', syncGarageBadges);
  syncGarageBadges();

  function getActiveVehicle() {
    const id = getActiveId();
    return getGarage().find((v) => v.id === id) || null;
  }

  /* ── Tire size tab + Wheel size tab ─────────────────────────────────── */
  const tirePanelEl  = dialog.querySelector('[data-vfm-tire-panel]');
  const wheelPanelEl = dialog.querySelector('[data-vfm-wheel-panel]');
  const tabs         = dialog.querySelectorAll('[data-vfm-tab]');

  if (tabs.length) {
    // Read header title/subtitle from data attributes on each tab button so
    // merchants can override them via the Theme Customizer without touching JS.
    const updateModalHeader = (name) => {
      const activeTab  = dialog.querySelector(`[data-vfm-tab="${name}"]`);
      const titleEl    = dialog.querySelector('[data-vfm-header-title]');
      const subtitleEl = dialog.querySelector('[data-vfm-header-subtitle]');
      if (titleEl)    titleEl.textContent    = activeTab?.dataset.tabTitle    || '';
      if (subtitleEl) subtitleEl.textContent = activeTab?.dataset.tabSubtitle || '';
      dialog.querySelectorAll('[data-vfm-icon]').forEach((icon) => {
        icon.classList.toggle('is-active', icon.dataset.vfmIcon === name);
      });
    };

    const setTab = (name) => {
      updateModalHeader(name);
      tabs.forEach((t) => {
        t.classList.toggle('is-active', t.dataset.vfmTab === name);
        t.setAttribute('aria-selected', String(t.dataset.vfmTab === name));
      });
      const isTire  = name === 'tire';
      const isWheel = name === 'wheel';
      if (tirePanelEl) tirePanelEl.hidden = !isTire;
      if (wheelPanelEl) wheelPanelEl.hidden = !isWheel;
      const tabsEl = dialog.querySelector('[data-vfm-tabs]');
      if (isTire || isWheel) {
        dialog.querySelectorAll('[data-vfm-screen]').forEach((s) => { s.hidden = true; });
        dialog.querySelector('[data-vfm-back]').hidden = true;
        if (tabsEl) tabsEl.hidden = false;
      } else {
        screenStack = [name === 'vin' ? 'vin' : 'home'];
        showScreen(screenStack[0]);
      }
    };

    tabs.forEach((tab) => tab.addEventListener('click', () => setTab(tab.dataset.vfmTab)));

    // Reset to vehicle tab on close
    dialog.addEventListener('close', () => {
      if (tirePanelEl) tirePanelEl.hidden = true;
      if (wheelPanelEl) wheelPanelEl.hidden = true;
      tabs.forEach((t) => {
        t.classList.toggle('is-active', t.dataset.vfmTab === 'vehicle');
        t.setAttribute('aria-selected', String(t.dataset.vfmTab === 'vehicle'));
      });
    });

    /* ── "Where do I find my tire size?" diagram toggle ── */
    const helpToggle = tirePanelEl?.querySelector('[data-tsf-help-toggle]');
    const diagramEl  = tirePanelEl?.querySelector('[data-tsf-diagram]');
    if (helpToggle && diagramEl) {
      helpToggle.addEventListener('click', () => {
        const hidden = diagramEl.hidden;
        diagramEl.hidden = !hidden;
        helpToggle.setAttribute('aria-expanded', String(hidden));
      });
    }

    /* ── Rear tire checkbox ── */
    const rearToggle = tirePanelEl?.querySelector('[data-tsf-rear-toggle]');
    const rearPanel  = tirePanelEl?.querySelector('[data-tsf-rear]');
    if (rearToggle && rearPanel) {
      rearToggle.addEventListener('change', () => { rearPanel.hidden = !rearToggle.checked; });
    }

    /* ── Tire size: View Results ── */
    const tsfSubmit = tirePanelEl?.querySelector('[data-tsf-submit]');
    const tsfErr    = tirePanelEl?.querySelector('[data-tsf-err]');
    if (tsfSubmit) {
      tsfSubmit.addEventListener('click', () => {
        const w = tirePanelEl.querySelector('[data-tsf-w]')?.value;
        const r = tirePanelEl.querySelector('[data-tsf-r]')?.value;
        const d = tirePanelEl.querySelector('[data-tsf-d]')?.value;

        if (!w || !r || !d) {
          if (tsfErr) tsfErr.hidden = false;
          return;
        }
        if (tsfErr) tsfErr.hidden = true;

        const rearOn = rearToggle?.checked;
        const rw = rearOn ? tirePanelEl.querySelector('[data-tsf-rw]')?.value : '';
        const rr = rearOn ? tirePanelEl.querySelector('[data-tsf-rr]')?.value : '';
        const rd = rearOn ? tirePanelEl.querySelector('[data-tsf-rd]')?.value : '';

        // Build search query in standard tire size notation (e.g. 225/45R17)
        // Replace `data-tire-collection` on the dialog with a collection handle to
        // use Shopify native filter URLs instead of site search.
        const tireCollection = dialog.dataset.tireCollection || '';
        let url;
        if (tireCollection) {
          const params = new URLSearchParams({
            'filter.p.m.tire_width': w,
            'filter.p.m.tire_ratio': r,
            'filter.p.m.tire_diameter': d,
          });
          url = `/collections/${tireCollection}?${params}`;
        } else {
          const frontSize = `${w}/${r}R${d}`;
          const rearSize  = rearOn && rw && rr && rd ? ` ${rw}/${rr}R${rd}` : '';
          url = `/search?type=product&q=${encodeURIComponent(frontSize + rearSize)}`;
        }

        dialog.close();
        window.location.href = url;
      });
    }

    /* ── Tire size: Clear ── */
    const tsfClear = tirePanelEl?.querySelector('[data-tsf-clear]');
    if (tsfClear) {
      tsfClear.addEventListener('click', () => {
        ['[data-tsf-w]', '[data-tsf-r]', '[data-tsf-d]', '[data-tsf-rw]', '[data-tsf-rr]', '[data-tsf-rd]'].forEach((s) => {
          const el = tirePanelEl.querySelector(s);
          if (el) el.value = '';
        });
        if (rearToggle) rearToggle.checked = false;
        if (rearPanel)  rearPanel.hidden   = true;
        if (tsfErr)     tsfErr.hidden      = true;
      });
    }

    /* ── Wheel size: Clear + View Results ── */
    if (wheelPanelEl) {
      const wsfErr      = wheelPanelEl.querySelector('[data-wsf-err]');
      const wsfClear    = wheelPanelEl.querySelector('[data-wsf-clear]');
      const wsfSubmit   = wheelPanelEl.querySelector('[data-wsf-submit]');
      const wsfDiamSel  = wheelPanelEl.querySelector('[data-wsf-d]');
      const wsfWidthSel = wheelPanelEl.querySelector('[data-wsf-w]');

      // Industry-standard width ranges per wheel diameter.
      // Source: wheel fitment guides (Tire Rack, Discount Tire et al.)
      const WSF_WIDTHS_FOR_DIAM = {
        '12':   ['4', '4.5', '5.5'],
        '13':   ['4.5', '5.5', '6', '6.5'],
        '14':   ['5.5', '6', '6.5', '6.75', '7', '7.5'],
        '15':   ['5.5', '6', '6.5', '6.75', '7', '7.5', '8'],
        '16':   ['6', '6.5', '6.75', '7', '7.5', '8', '8.25', '8.5', '8.75'],
        '17':   ['6', '6.5', '6.75', '7', '7.5', '8', '8.25', '8.5', '8.75', '9', '9.5'],
        '17.5': ['5.5', '6', '6.5', '6.75', '7', '7.5'],
        '18':   ['7', '7.5', '8', '8.25', '8.5', '8.75', '9', '9.5', '9.75', '10', '10.5', '11'],
        '19':   ['7.5', '8', '8.25', '8.5', '8.75', '9', '9.5', '9.75', '10', '10.5', '11'],
        '19.5': ['5.5', '6', '6.5', '6.75', '7', '7.5'],
        '20':   ['8', '8.25', '8.5', '8.75', '9', '9.5', '9.75', '10', '10.5', '11', '11.5', '12', '12.5'],
        '21':   ['8', '8.25', '8.5', '8.75', '9', '9.5', '9.75', '10', '10.5', '11', '11.5', '12', '12.5'],
        '22':   ['9', '9.5', '9.75', '10', '10.5', '11', '11.5', '12', '12.5', '14'],
        '24':   ['9', '9.5', '9.75', '10', '10.5', '11', '11.5', '12', '12.5', '14'],
        '26':   ['9.5', '9.75', '10', '10.5', '11', '11.5', '12', '12.5', '14'],
      };

      // Snapshot the full width list once at load time so Clear All can restore it.
      const allWidthOpts = wsfWidthSel
        ? [...wsfWidthSel.querySelectorAll('option')].filter((o) => o.value !== '')
        : [];

      const rebuildWidths = (diam) => {
        if (!wsfWidthSel) return;
        const prev     = wsfWidthSel.value;
        const validSet = diam && WSF_WIDTHS_FOR_DIAM[diam]
          ? new Set(WSF_WIDTHS_FOR_DIAM[diam])
          : null; // null → show all

        wsfWidthSel.innerHTML = '<option value="" disabled selected>Select width</option>';
        allWidthOpts.forEach((opt) => {
          if (!validSet || validSet.has(opt.value)) {
            wsfWidthSel.appendChild(opt.cloneNode(true));
          }
        });
        // Preserve prior selection only if it's still valid for the new diameter
        if (prev && (!validSet || validSet.has(prev))) wsfWidthSel.value = prev;
      };

      wsfDiamSel?.addEventListener('change', () => rebuildWidths(wsfDiamSel.value));

      if (wsfClear) {
        wsfClear.addEventListener('click', () => {
          ['[data-wsf-d]', '[data-wsf-bp]', '[data-wsf-w]'].forEach((s) => {
            const el = wheelPanelEl.querySelector(s);
            if (el) el.value = '';
          });
          rebuildWidths(''); // restore full width list
          if (wsfErr) wsfErr.hidden = true;
        });
      }

      if (wsfSubmit) {
        wsfSubmit.addEventListener('click', () => {
          const d  = wheelPanelEl.querySelector('[data-wsf-d]')?.value;
          const bp = wheelPanelEl.querySelector('[data-wsf-bp]')?.value;
          const w  = wheelPanelEl.querySelector('[data-wsf-w]')?.value;

          if (!d && !bp && !w) {
            if (wsfErr) wsfErr.hidden = false;
            return;
          }
          if (wsfErr) wsfErr.hidden = true;

          // Strip the inch-equivalent parenthetical to get the bare metric value
          // e.g. "5x114.3mm (5x4.5&quot;)" → "5x114.3mm"
          const bpMetric = bp ? bp.split(' (')[0] : '';
          const wheelCollection = dialog.dataset.wheelCollection || '';
          let url;

          if (wheelCollection) {
            const params = new URLSearchParams();
            if (d)  params.set('filter.p.m.wheel_diameter',    d);
            if (bp) params.set('filter.p.m.wheel_bolt_pattern', bpMetric);
            if (w)  params.set('filter.p.m.wheel_width',       w);
            url = `/collections/${wheelCollection}?${params}`;
          } else {
            const parts = [d && `${d}"`, bpMetric, w && `${w}"`].filter(Boolean);
            url = `/search?type=product&q=${encodeURIComponent(parts.join(' '))}`;
          }

          dialog.close();
          window.location.href = url;
        });
      }
    }
  }
})();
